import { FC } from 'react';
import {
  Badge,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import { NavItem } from 'src/types/NavBarTypes';
import { NAV_BADGE_SX } from './RenterNavBarMobile';

interface DesktopNavBarButtonProps {
  isActive: boolean;
  item: NavItem;
  onClick?: () => void;
}

const DesktopNavBarButton: FC<DesktopNavBarButtonProps> = ({
  isActive,
  item,
  onClick,
}) => {
  const expanded = useSelector(
    (state: RootState) => state.newRenterUI.expanded
  );

  const iconContent = (
    <ListItemIcon
      sx={{
        minWidth: 0,
        mr: expanded ? 2 : 'auto',
        justifyContent: 'center',
        color: isActive ? 'common.white' : 'inherit',
        position: 'relative',
      }}
    >
      <Badge
        badgeContent={item.dotBadge ? undefined : item.badge ?? 0}
        variant={item.dotBadge ? 'dot' : 'standard'}
        invisible={item.dotBadge ? false : !(item.badge ?? 0)}
        color="error"
        max={99}
        sx={NAV_BADGE_SX}
      >
        {item.icon}
      </Badge>
      {item.external && (
        <OpenInNewIcon
          color="info"
          sx={{
            fontSize: 14,
            ml: 'auto',
            position: 'absolute',
            top: 0,
            left: '100%',
          }}
        />
      )}
    </ListItemIcon>
  );

  return (
    <ListItem component="li" disablePadding>
      <ListItemButton
        selected={isActive}
        onClick={onClick}
        aria-label={
          item.dotBadge
            ? `${item.label}, action required`
            : item.badge
            ? `${item.label}, ${item.badge} pending`
            : item.label
        }
        sx={{
          justifyContent: expanded ? 'initial' : 'center',
          px: 2,
          minHeight: 48,
          borderRadius: 1.5,
          mx: 0.5,
          ...(isActive && {
            backgroundColor: 'rgba(255,255,255,0.12)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)' },
            '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.12)' },
            '&.Mui-selected:hover': {
              backgroundColor: 'rgba(255,255,255,0.18)',
            },
          }),
        }}
      >
        {expanded ? (
          iconContent
        ) : (
          <Tooltip title={item.label} placement="right" disableInteractive>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              {iconContent}
            </span>
          </Tooltip>
        )}
        {expanded && (
          <ListItemText
            primary={item.label}
            slotProps={{
              primary: {
                color: isActive ? 'common.white' : 'inherit',
                variant: 'body2',
                fontWeight: isActive ? 600 : 400,
              },
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
};

export default DesktopNavBarButton;
